import { ref, watch, type Ref, computed } from 'vue'
import { usePitcherApi, useAdmin } from '@lib/sdk/main'

interface Comment {
  id: string
  content: string
  author?: string
  authorId?: string | number
  authorEmail?: string
  createdAt: string
}

interface Thread {
  id: string
  content: string
  author: string
  authorId?: string | number
  authorEmail?: string
  createdAt: string
  resolved: boolean
  canvasId?: string
  componentId?: string
  assignee?: any
  comments: Comment[]
}

interface StoredCommentsData {
  canvasId: string
  threads: Thread[]
  lastUpdated: string
}

// Global storage for all canvas comments
const globalStorage = new Map<
  string,
  {
    threads: Ref<Thread[]>
    isLoading: Ref<boolean>
    isSaving: Ref<boolean>
    lastSaved: Ref<Date | null>
    storageId: string | null
    subscribers: number
  }
>()

// API credentials (shared across all instances)
let instanceId: string | null = null
let accessToken: string | null = null
let apiInitialized = false

const API_BASE_PATH = '/core/api/protected/appsdb'

// Initialize API credentials
const initializeApi = async () => {
  if (apiInitialized) return true

  try {
    let api: any = usePitcherApi()
    if (window.location.href.indexOf('/admin/') !== -1) {
      api = useAdmin()
    }

    const env = await api.getEnv()
    instanceId = env.pitcher.instance.id || null
    accessToken = env.pitcher.access_token || null

    if (!instanceId || !accessToken) {
      console.error('Failed to get instance ID or access token')
      return false
    }

    apiInitialized = true
    return true
  } catch (error) {
    console.error('Error initializing comments storage:', error)
    return false
  }
}

// Generic API fetch method
const apiFetch = async (endpoint: string, options: RequestInit & { body?: any } = {}) => {
  if (!apiInitialized) {
    await initializeApi()
  }

  const url = API_BASE_PATH + endpoint
  const headers = {
    'Content-Type': 'application/json',
    'x-instance-id': instanceId!,
    Authorization: `Bearer ${accessToken}`,
    ...(options.headers || {}),
  }

  const { body, ...otherOptions } = options
  const fetchOptions: RequestInit = {
    ...otherOptions,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  }

  try {
    const response = await fetch(url, fetchOptions)

    if (response.status === 204) {
      return { status: response.status, data: null }
    }

    const data = await response.json()

    if (response.status >= 400) {
      console.error('Comments API Error:', {
        status: response.status,
        url,
        method: fetchOptions.method,
        response: data,
      })
    }

    return { status: response.status, data }
  } catch (error) {
    console.error('Comments API Request Error:', error)
    throw error
  }
}

export function useSharedCommentsStorage(canvasId: Ref<string | undefined>) {
  // Get or create storage for this canvas
  const getOrCreateStorage = (id: string) => {
    if (!globalStorage.has(id)) {
      globalStorage.set(id, {
        threads: ref<Thread[]>([]),
        isLoading: ref(false),
        isSaving: ref(false),
        lastSaved: ref<Date | null>(null),
        storageId: null,
        subscribers: 0,
      })
    }
    return globalStorage.get(id)!
  }

  // Current storage based on canvas ID
  let currentStorage = canvasId.value ? getOrCreateStorage(canvasId.value) : null

  // Computed refs that automatically update when storage changes
  const threads = computed(() => {
    if (!canvasId.value || !globalStorage.has(canvasId.value)) {
      return []
    }
    return globalStorage.get(canvasId.value)!.threads.value
  })

  const isLoading = computed(() => {
    if (!canvasId.value || !globalStorage.has(canvasId.value)) {
      return false
    }
    return globalStorage.get(canvasId.value)!.isLoading.value
  })

  const isSaving = computed(() => {
    if (!canvasId.value || !globalStorage.has(canvasId.value)) {
      return false
    }
    return globalStorage.get(canvasId.value)!.isSaving.value
  })

  const lastSaved = computed(() => {
    if (!canvasId.value || !globalStorage.has(canvasId.value)) {
      return null
    }
    return globalStorage.get(canvasId.value)!.lastSaved.value
  })

  // Increment subscriber count
  if (currentStorage) {
    currentStorage.subscribers++
  }

  // Save comments to backend
  const saveComments = async () => {
    if (!canvasId.value || !currentStorage || currentStorage.isSaving.value) {
      return
    }

    currentStorage.isSaving.value = true

    try {
      const data: StoredCommentsData = {
        canvasId: canvasId.value,
        threads: currentStorage.threads.value,
        lastUpdated: new Date().toISOString(),
      }

      if (!currentStorage.storageId) {
        // Create new entry
        const response = await apiFetch('', {
          method: 'POST',
          body: {
            type: 'canvas_comments',
            instance_id: instanceId,
            no_user_id: true,
            data,
          },
        } as any)

        if (response.status === 200 || response.status === 201) {
          currentStorage.storageId = response.data.id
          currentStorage.lastSaved.value = new Date()
        }
      } else {
        // Update existing entry
        const response = await apiFetch(`/${currentStorage.storageId}`, {
          method: 'PUT',
          body: {
            type: 'canvas_comments',
            instance_id: instanceId,
            no_user_id: true,
            data,
          },
        } as any)

        if (response.status === 200) {
          currentStorage.lastSaved.value = new Date()
        }
      }
    } catch (error) {
      console.error('Failed to save comments:', error)
    } finally {
      currentStorage.isSaving.value = false
    }
  }

  // Load comments from backend
  const loadComments = async (silent = false) => {
    if (!canvasId.value || !currentStorage || currentStorage.isLoading.value) return

    // Silent mode for periodic updates - don't show loading state
    if (!silent) {
      currentStorage.isLoading.value = true
    }

    try {
      // Search for comments by canvas ID
      const response = await apiFetch(`?type=canvas_comments`, {
        method: 'GET',
      })

      if (response.status === 200 && response.data.entries) {
        // Find comments for this canvas
        const entry = response.data.entries.find((e: any) => e.data?.canvasId === canvasId.value)

        if (entry) {
          currentStorage.storageId = entry.id

          // Smart merge to preserve local state
          const newThreads = entry.data.threads || []
          const existingThreads = currentStorage.threads.value

          // Create a map of existing threads by ID for quick lookup
          const existingMap = new Map(existingThreads.map((t) => [t.id, t]))

          // Merge threads intelligently
          const mergedThreads: Thread[] = []

          newThreads.forEach((newThread: Thread) => {
            const existing = existingMap.get(newThread.id)
            if (existing) {
              // Update existing thread while preserving any local state
              // Only update if the thread has actually changed
              if (JSON.stringify(existing) !== JSON.stringify(newThread)) {
                Object.assign(existing, newThread)
              }
              mergedThreads.push(existing)
              existingMap.delete(newThread.id)
            } else {
              // This is a new thread
              mergedThreads.push(newThread)
            }
          })

          // Remove threads that no longer exist on the server
          // (threads left in existingMap were not in the new data)

          currentStorage.threads.value = mergedThreads
          currentStorage.lastSaved.value = new Date(entry.data.lastUpdated)
        } else if (!silent) {
          // Only clear threads on initial load, not on silent updates
          currentStorage.threads.value = []
        }
      }
    } catch (error) {
      console.error('Failed to load comments:', error)
      if (!silent) {
        currentStorage.threads.value = []
      }
    } finally {
      if (!silent) {
        currentStorage.isLoading.value = false
      }
    }
  }

  // Delete all comments for a canvas
  const deleteComments = async () => {
    if (!currentStorage?.storageId) return

    try {
      const response = await apiFetch(`/${currentStorage.storageId}`, {
        method: 'DELETE',
      })

      if (response.status === 204 || response.status === 200) {
        currentStorage.threads.value = []
        currentStorage.storageId = null
        currentStorage.lastSaved.value = null
        console.log('Comments deleted')
      }
    } catch (error) {
      console.error('Failed to delete comments:', error)
    }
  }

  // Watch canvas ID changes
  watch(
    canvasId,
    (newId, oldId) => {
      // Decrement old storage subscriber count
      if (oldId && globalStorage.has(oldId)) {
        const oldStorage = globalStorage.get(oldId)!
        oldStorage.subscribers--
      }

      if (newId) {
        currentStorage = getOrCreateStorage(newId)
        currentStorage.subscribers++

        // Load comments for new canvas if not already loaded
        if (currentStorage.threads.value.length === 0 && !currentStorage.isLoading.value) {
          loadComments()
        }
      } else {
        currentStorage = null
      }
    },
    { immediate: true },
  )

  return {
    threads,
    isLoading,
    isSaving,
    lastSaved,
    saveComments,
    loadComments,
    deleteComments,
  }
}
